import React from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { type SelectOptionProps } from '@amzn/meridian/select/select-option';
import Text from '@amzn/meridian/text';

import { AUSTIN_ONLY_WEBHOOK_EVENTS, WEBHOOK_EVENT_DESCRIPTIONS_MAP, WEBHOOK_EVENT_TITLES_MAP } from '../constants';
import { type SiteWebhook, type SiteWebhookFormEntry, type WEBHOOK_EVENT_IDS, type WebhookFormErrors } from '../types';

import BlankWebhookRow from './blankWebhookRow';
import WebhookRow, { type WebhookRowProps } from './webhookRow';

export interface WebhookEventCardProps {
  webhookEventId: WEBHOOK_EVENT_IDS;
  isEditing: boolean;
  accommodationTypeSelectOptions: SelectOptionProps[];
  loadedSiteWebhooks: SiteWebhook[]; // readonly state returned from backend
  siteWebhookFormEntries: SiteWebhookFormEntry[]; // workspace for edits
  errors: WebhookFormErrors;
  addWebhook: (webhookEventId: WEBHOOK_EVENT_IDS) => void;
  updateWebhook: (webhookIndex: number, update: SiteWebhookFormEntry) => void;
  removeWebhook: (webhookIndex: number) => void;
  clearWebhook: (webhookIndex: number) => void;
}

const WebhookEventCard = ({
  webhookEventId,
  isEditing,
  accommodationTypeSelectOptions,
  loadedSiteWebhooks,
  siteWebhookFormEntries,
  errors,
  addWebhook,
  updateWebhook,
  removeWebhook,
  clearWebhook,
}: WebhookEventCardProps) => {
  const webhooksForDisplay = isEditing ? siteWebhookFormEntries : loadedSiteWebhooks;
  // filter webhooks to this card's event type
  // webhookIndex in full list is used for updates, clears, removals
  const webhooksAndIndexesForEvent = webhooksForDisplay
    .map((webhook, webhookIndex) => {
      return { webhookIndex, webhook };
    })
    .filter(({ webhook }) => webhook.webhookEventId === webhookEventId);

  const onRemove = (webhookIndex: number) => {
    if (webhooksAndIndexesForEvent.length === 1) {
      clearWebhook(webhookIndex);
    } else {
      removeWebhook(webhookIndex);
    }
  };

  const austinOnly = AUSTIN_ONLY_WEBHOOK_EVENTS.includes(webhookEventId);

  return (
    <>
      <Box spacingInset="300" />
      <Card disabled={true} aria-labelledby={`webhook-event-section-header-${webhookEventId}`}>
        {/* Card header content */}
        <Row widths={['fill', 'grid-2']}>
          <Heading level={1} type="h500" id={`webhook-event-section-header-${webhookEventId}`}>
            {WEBHOOK_EVENT_TITLES_MAP[webhookEventId]}
          </Heading>
          <Button type="link" onClick={() => addWebhook(webhookEventId)} disabled={!isEditing}>
            Add URL
          </Button>
        </Row>
        <Text color="secondary">"{WEBHOOK_EVENT_DESCRIPTIONS_MAP[webhookEventId]}"</Text>
        <Box spacingInset="200" />
        {/* Webhook form rows */}
        {webhooksAndIndexesForEvent.length < 1 && <BlankWebhookRow austinOnly={austinOnly} />}
        {webhooksAndIndexesForEvent.map(({ webhookIndex, webhook }, rowIndex) => {
          const urlNum = rowIndex + 1;
          const rowProps: WebhookRowProps = {
            urlNum,
            isEditing,
            accommodationTypeSelectOptions,
            webhookIndex,
            webhook,
            austinOnly,
            errors,
            updateWebhook,
            onRemove,
          };
          return <WebhookRow {...rowProps} key={webhookIndex} />;
        })}
      </Card>
    </>
  );
};

export default WebhookEventCard;
